import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { session } from '@/lib/api/session';
import type { AuthUser } from '@/lib/api/types';

/**
 * Auth CLIENT state.
 *
 * Deliberately holds only session identity — never server data. Lists, records
 * and stats belong to TanStack Query; duplicating them here would create two
 * caches that drift apart. Redux answers exactly one question: "who is signed
 * in, and what may they do?"
 */

export interface AuthState {
  user: AuthUser | null;
  /** Hydrated from localStorage so a reload doesn't flash the login screen. */
  isAuthenticated: boolean;
  permissions: string[];
}

const initialState: AuthState = {
  user: session.user,
  isAuthenticated: session.isAuthenticated,
  permissions: session.user?.permissions ?? [],
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    signedIn(state, action: PayloadAction<AuthUser>) {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.permissions = action.payload.permissions ?? [];
    },
    /** Refresh identity/permissions from `/auth/me` without a full sign-in. */
    userLoaded(state, action: PayloadAction<AuthUser>) {
      state.user = action.payload;
      state.permissions = action.payload.permissions ?? state.permissions;
    },
    signedOut(state) {
      state.user = null;
      state.isAuthenticated = false;
      state.permissions = [];
    },
  },
});

export const { signedIn, userLoaded, signedOut } = authSlice.actions;
export default authSlice.reducer;
